import { zDecimalNullable, zDecimalRequired } from '@harvestiq/zod';
import { z } from '@harvestiq/zod';

// TODO: Convert to Decimal
const priceSchema = z.object({
  adjustmentFactor: z.coerce.number().nullable(),
  lastPrice: z.coerce.number().nullable(),
  adjustedLastPrice: z.coerce.number().nullable(),
  closePrice: z.coerce.number().nullable(),
  adjustedClosePrice: z.coerce.number().nullable(),
  expirationDate: z.coerce.date().nullable(),
  symbol: z.string().nullable(),
  name: z.string().nullable(),
  reportPeriod: z.coerce.date().nullable(),
});

export const lrpCoverageDetailsSchema = z.object({
  actualEndingValue: z.coerce.number().nullable().optional(),
  actualIndemnity: z.coerce.number().nullable().optional(),
  futuresIndemnity: z.coerce.number().nullable().optional(),
  cmeCashPrice: z.coerce.number().nullable().optional(),
  coverageLevel: z.coerce.number(),
  coveragePrice: z.coerce.number(),
  endDate: z.string(),
  endorsementLength: z.coerce.number().nullable().optional(),
  expectedEndingValue: z.coerce.number().nullable(), // null if actualEndingValue is present
  expectedIndemnity: z.coerce.number().nullable().optional(),
  numberofHead: z.coerce.number(), // [sic]
  premium: z.coerce.number(),
  targetWeight: z.coerce.number(),
  price: z
    .object({
      futures: priceSchema,
      cash: priceSchema,
    })
    .optional(),

  // m13 ingestion fields
  reinsurance_year: z.string().nullable().optional(),
  record_type_code: z.string().nullable().optional(),
  aip_policy_producer_key: z.string().nullable().optional(),
  aip_insurance_in_force_key: z.string().nullable().optional(),
  aip_lrp_premium_key: z.string().nullable().optional(),
  aip_insurance_agent_key: z.string().nullable().optional(),
  practice_code: z.string().nullable().optional(),
  commodity_type_code: z.string().nullable().optional(),
  class_code: z.string().nullable().optional(),
  subclass_code: z.string().nullable().optional(),
  intended_use_code: z.string().nullable().optional(),
  irrigation_practice_code: z.string().nullable().optional(),
  cropping_practice_code: z.string().nullable().optional(),
  organic_practice_code: z.string().nullable().optional(),
  interval_code: z.string().nullable().optional(),
  insured_share_percent: z.string().nullable().optional(),
  aip_liability_amount: z.string().nullable().optional(),
  aip_total_premium_amount: z.string().nullable().optional(),
  aip_subsidy_amount: z.string().nullable().optional(),
  additional_subsidy_flag: z.string().nullable().optional(),
  state_private_subsidy_flag: z.string().nullable().optional(),
  settlement_flag: z.string().nullable().optional(),
  cc_subsidy_reduction_percent: z.string().nullable().optional(),
});
export type LrpCoverageDetails = z.infer<typeof lrpCoverageDetailsSchema>;

export const lgmDetailsMarketingMonthsSchema = z.object({
  marketingMonth: z.string(),
  numberofHead: z.number(), // [sic]
  premium: zDecimalRequired(),
  subsidy: zDecimalRequired(),
  // Monthly Gross Margin Guarantee (per head) = Monthly Expected Gross Margin (per head, see grossMarginGuarantee below) - deductible
  grossMarginGuarantee: zDecimalRequired(),
  // Monthly expected gross margin per head
  expectedGrossMargin: zDecimalNullable().optional(),

  // Added via calculations
  actualGrossMargin: zDecimalNullable().optional(),
  actualGrossMarginChange: zDecimalNullable().optional(),
  projectedGrossMargin: zDecimalNullable().optional(),
  projectedGrossMarginChange: zDecimalNullable().optional(),

  // extra fields from M13 P16 - month specific
  corn_equivalent_amount: z.number().optional(),
  soybean_meal_equivalent_amount: z.number().optional(),
});
export type LgmDetailsMarketingMonths = z.infer<
  typeof lgmDetailsMarketingMonthsSchema
>;

export const lgmCoverageDetailsSchema = z.object({
  deductible: z.coerce.number().optional(),
  marketingMonths: z.array(lgmDetailsMarketingMonthsSchema),

  // extra fields from M13 P16
  aip_liability_amount: z.string().nullable().optional(),
  aip_total_premium_amount: z.string().nullable().optional(),
  aip_subsidy_amount: z.string().nullable().optional(),
  additional_subsidy_flag: z.string().nullable().optional(),
  state_private_subsidy_flag: z.string().nullable().optional(),
  settlement_flag: z.string().nullable().optional(),
  cc_subsidy_reduction_percent: z.string().nullable().optional(),
  practice_code: z.string().nullable().optional(),
  commodity_type_code: z.string().nullable().optional(),
  class_code: z.string().nullable().optional(),
  subclass_code: z.string().nullable().optional(),
  intended_use_code: z.string().nullable().optional(),
  irrigation_practice_code: z.string().nullable().optional(),
  cropping_practice_code: z.string().nullable().optional(),
  organic_practice_code: z.string().nullable().optional(),
  interval_code: z.string().nullable().optional(),
  reinsurance_year: z.string().nullable().optional(),
  record_type_code: z.string().nullable().optional(),
  aip_policy_producer_key: z.string().nullable().optional(),
  aip_insurance_in_force_key: z.string().nullable().optional(),
  aip_lgm_premium_key: z.string().nullable().optional(),
  aip_insurance_agent_key: z.string().nullable().optional(),
});
export type LgmCoverageDetails = z.infer<typeof lgmCoverageDetailsSchema>;

export const coverageDetailsSchema = z.union([
  lrpCoverageDetailsSchema,
  lgmCoverageDetailsSchema,
]);
export type CoverageDetails = z.infer<typeof coverageDetailsSchema>;
